/**
 * WHAT IS COVERING THE SCREEN, AND WHAT IS ON TOP OF THAT.
 *
 * A surface that dims the page puts everything beneath it out of play: the
 * screen's own letters go quiet, Escape belongs to the dialogue, and a `/`
 * reaches the dialogue's search box rather than the one behind it. Every
 * mechanism that needs to know asks here, so "is anything open" has one answer.
 *
 * A DIALOGUE REGISTERS ITSELF. The alternative — reading the DOM for something
 * that looks like an overlay — makes the rule an appearance rather than a
 * declaration, and a drawer built out of different parts then silently goes
 * without. The kit's Modal and Drawer register; a POPOVER does not, because an
 * `i` or a tooltip is a thing you glance at beside the work and trapping the
 * keyboard in one makes a footnote into a room you have to leave.
 *
 * NEWEST ON TOP, and "newest" is mount order rather than a z-index anybody types
 * in: a dialogue opened from a dialogue is mounted after it, which is what the
 * layering already means.
 */

const stack: HTMLElement[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

/** Watch the stack. Returns the unsubscribe. */
export function subscribeOverlays(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Declare a covering surface for as long as it is mounted. Returns the removal.
 *
 * Re-registering the same node moves it to the top, which is what a dialogue
 * that re-mounts its content means.
 */
export function registerOverlay(node: HTMLElement): () => void {
  const existing = stack.indexOf(node);
  if (existing >= 0) stack.splice(existing, 1);
  stack.push(node);
  emit();
  return () => {
    const at = stack.indexOf(node);
    if (at < 0) return;
    stack.splice(at, 1);
    emit();
  };
}

/** How many surfaces are covering the page. Nought means the screen is live. */
export function overlayDepth(): number {
  return stack.filter((node) => node.isConnected).length;
}

/** The surface a keystroke belongs to: the newest one still on screen, or null. */
export function topOverlay(): HTMLElement | null {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const node = stack[i];
    if (node?.isConnected) return node;
  }
  return null;
}

/** For tests. */
export function resetOverlays(): void {
  stack.length = 0;
  emit();
}
